import React from 'react';
import { useCss, keyframes, IStyles } from 'app/utils/css';
import { PURE_WHITE, GREY_I, LINK_COLOR } from 'app/constants/Colors';

export enum ColorScheme {
  Dark,
  Light,
  Link,
}

export enum SpinnerSize {
  Tiny,
  Small,
  Normal,
}

interface IProps {
  color?: ColorScheme;
  size?: SpinnerSize;
  /**
   * Use `display: block` instead of `inline-block` which is the
   * default.
   *
   * This is useful in situation when extra space below text baseline
   * messes up positioning, for example when centring vertically.
   */
  displayAsBlock?: boolean;
}

// CSS and HTML code based on example at https://loading.io/css/
export const Spinner: React.SFC<IProps> = ({
  color = ColorScheme.Light,
  size = SpinnerSize.Normal,
  displayAsBlock = false,
}) => {
  const css = useCss();
  const ringClassName = css('ring', [
    {
      display: displayAsBlock ? 'block' : 'inline-block',
      position: 'relative',
    },
    ringSizes[size],
  ]);
  const childClassName = css('child', [
    {
      boxSizing: 'border-box',
      display: 'block',
      position: 'absolute',
    },
    childSizes[size],
    {
      borderColor: `${(
        color === ColorScheme.Dark ? GREY_I :
        color === ColorScheme.Link ? LINK_COLOR :
        /* else */ PURE_WHITE
      )} transparent transparent transparent`,
      borderRadius: '50%',
      animation: 'lds-animation 1.5s cubic-bezier(0.2, 0.2, 0.2, 1) infinite',
      animationName: spinnerKeyFrames,
      ':nth-of-type(1)': {
        animationDelay: '-0.45s',
      },
      ':nth-of-type(2)': {
        animationDelay: '-0.3s',
      },
      ':nth-of-type(3)': {
        animationDelay: '-0.15s',
      },
    },
  ]);

  return (
    <div className={ringClassName}>
      <div className={childClassName} />
      <div className={childClassName} />
      <div className={childClassName} />
      <div className={childClassName} />
    </div>
  );
};

const spinnerKeyFrames = keyframes({
  '0%': {
    transform: 'rotate(0deg)',
  },
  '100%': {
    transform: 'rotate(360deg)',
  },
});

const ringSizes: { [K in SpinnerSize]: Pick<IStyles, 'width' | 'height'> } = {
  [SpinnerSize.Tiny]: {
    width: '20px',
    height: '20px',
  },
  [SpinnerSize.Small]: {
    width: '33px',
    height: '33px',
  },
  [SpinnerSize.Normal]: {
    width: '64px',
    height: '64px',
  },
};

const childSizes: { [K in SpinnerSize]: Pick<IStyles, 'width' | 'height' | 'margin' | 'border'> } = {
  [SpinnerSize.Tiny]: {
    width: '15px',
    height: '15px',
    margin: '3px',
    border: '2px solid',
  },
  [SpinnerSize.Small]: {
    width: '25px',
    height: '25px',
    margin: '4px',
    border: '3px solid',
  },
  [SpinnerSize.Normal]: {
    width: '51px',
    height: '51px',
    margin: '6px',
    border: '6px solid',
  },
};

export default Spinner;
